import React, { useState, useEffect } from 'react';
import { useLocation , useNavigate } from 'react-router-dom-v5-compat';
import { css } from 'styled-components';
import { Viewport, ViewSmall, ViewLarge } from '@shared-ui/viewport-context';

//components
import Layout from 'src/components/common/theme/Layout';
import {
  MenuItemConfig,
  PositionType,
  UitkMenu,
  UitkMenuContainer,
  UitkMenuList,
  UitkMenuTrigger,
} from 'uitk-react-menu';
import { TertiaryButton, TertiaryButtonProps } from 'src/components/common/ui/button/button';
import { HeaderBrandLogo, HeaderBrandLogoProps } from 'src/components/common/modules/header-brand-logo/brand-logo';
import LozengeMessageV2 from 'src/components/common/modules/global-header/lozenge-message/lozenge-message-v2';

//helpers
import { checkValue } from 'src/components/common/helper';
import { UitkIcon } from 'uitk-react-icons';
import { mediaStudioBaseUrl } from 'src/components/media-studio/utils/helper';

//types
import { SubHeaderProps } from 'src/components/common/modules/global-header/global-header.d';
import { UitkSheet, UitkSheetContent } from 'uitk-react-sheet';
import { UitkToolbar } from 'uitk-react-toolbar';
import { LozengeMessageV2Props } from 'src/components/common/module-util';

//configs
import * as config from 'src/config/config';
const urlPattern = config.config.media_store_url;

const SubMenuDropDown = (device: 'mobile' | 'desktop', navigationData: any, linkNavigationMethod: any) => {
  if (!checkValue(navigationData, true)) {
    return null;
  }

  const navigate = useNavigate();

  const getMenuItems = () => {
    const menuItemsArr: MenuItemConfig[] = [];
    const menuNavigationData = [
      ...navigationData,
      {
        title: 'Map',
        url: '/mstudiodev/journeys?mcicid=cp.cplite.mstudiodev',
        urlFriendlyName: 'journeys',
      },
    ];

    menuNavigationData?.forEach((mi) => {
      const { title, url } = mi;
      if (checkValue(title) && checkValue(url)) {
        const menu = {
          attributes: {
            role: 'menuitem',
            tabindex: 0,
          },
          className: 'uitk-list-item',
          label: title,
          onClick: (e) => onMenuItemClick(e, title, url),
        };
        menuItemsArr.push(menu);
      }
    });

    return menuItemsArr;
  };

  const goToPage = (pageUrl: string) => {
    const url = urlPattern.replace('/{page_name}', pageUrl);

    if (checkValue(linkNavigationMethod) && linkNavigationMethod === 'reload') {
      window.location.href = url;
    } else {
      navigate(`${url}`);
    }
  };

  const onMenuItemClick = (e, title, url) => {
    goToPage(url);
  };

  const [menuOpen, onMenuOpen] = useState(false);
  const menuItems = getMenuItems();

  return (
    <div
      {...(device === 'desktop' ? { onMouseEnter: () => onMenuOpen(true), onMouseLeave: () => onMenuOpen(false) } : {})}
    >
      <UitkMenu
        id="show8"
        isOpen={menuOpen}
        {...(device === 'mobile' ? { onTriggerClick: () => onMenuOpen(!menuOpen) } : {})}
        className={`secondary-header-v2-menu ${menuOpen ? 'open-menu' : ''}`}
      >
        <UitkMenuTrigger>
          <button className="dropdown-trigger-btn">
            Destinations
            <UitkIcon name="expand_more" />
          </button>
        </UitkMenuTrigger>
        <UitkMenuContainer
          position={PositionType.RIGHT}
          autoPosition={true}
          intersectionObserverRoot=".secondary-header-v2-region"
          width={300}
          className={`secondary-header-menu-container ${menuOpen ? 'secondary-header-menu-container-visibel' : ''}`}
        >
          <UitkMenuList items={menuItems} />
        </UitkMenuContainer>
      </UitkMenu>
    </div>
  );
};

const SubHeadervariant2: React.FC<SubHeaderProps> = (props) => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const triggerRef = React.useRef();
  const [isMobileMenuVisible, setIsMobileVisible] = useState(false);

  const baseUrl = mediaStudioBaseUrl();

  const { multiMenuItems, linkNavigationMethod } = props;
  const moduleCss = () => {
    return `
        ${
          checkValue(props?.partnerHeaderBackgroundColor && props?.partnerHeaderTextColor)
            ? `
          &.secondary-header-v2-region {
            background-color: ${props?.partnerHeaderBackgroundColor} !important;
            color:${props?.partnerHeaderTextColor} !important;

            .secondary-header-menu-container,
            .info-region,
            .mobile-menu-sheet,
            .mobile-menu-close {
                background-color: ${props?.partnerHeaderBackgroundColor} !important;
                color:${props?.partnerHeaderTextColor} !important;
                svg {
                  fill:${props?.partnerHeaderTextColor} !important;
                }
            }
          }
      `
            : ''
        }
         
        `;
  };

  const menuButtonProps: TertiaryButtonProps = {
    exClass: 'menu-button',
    alignLabelToContent: 'start',
    gaTracking: {
      eventCategory: 'Button Click',
      eventAction: `Secondary header mobile menu open`,
      eventLabel: `Secondary header mobile menu open`,
    },
    text: 'Menu',
    icon: { name: 'menu' },
    onClickEvent: () => setIsMobileVisible(!isMobileMenuVisible),
  };

  const headerBrandLogoProps: HeaderBrandLogoProps = {
    altText: 'partnerLogo',
    imgSrc: 'https://mesocp3.azureedge.net/media/cicjpnjy/microsoftteams-image.png?v=8-10-20225:51:19AM',
    href: '/',
    exClass: 'partner-logo',
    gaTracking: {
      eventCategory: 'logo click',
      eventAction: 'Partner Header',
      eventLabel: 'props?.partnerUrl,',
    },
    target: props?.openPartnerUrlInSameTab ? '_self' : '_blank',
  };

  const goToHome = () => {
    if (checkValue(linkNavigationMethod) && linkNavigationMethod === 'reload') {
      window.location.href = baseUrl;
    } else {
      navigate(`${baseUrl}`);
    }
  };

  const lozengeMessageProps: LozengeMessageV2Props = {
    lozengeDetailMessage: props?.lozengeDetailMessage,
    backgroundColor: checkValue(props?.lozengeBackgroundColor) ? props?.lozengeBackgroundColor : props?.partnerHeaderBackgroundColor,
    color: props?.lozengeTextColor,
    defaultTextColor: props?.partnerHeaderTextColor,
    exClass: 'sub-header-v2',
    iconStyle: props?.lozengeIconStyle,
    iconColor: props?.lozengeIconColor,
  };

  // Active progress animation
  useEffect(() => {
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  }, [location?.pathname]);


  return (
    <Layout
      HtmlTag="div"
      className="secondary-header-v2-region"
      css={css`
        ${moduleCss()}
      `}
    >
      <div className={`linePreloader ${isLoading ? 'animation' : ''}`}></div>
      <Viewport>
        {/* mobile secondary header device < 992 */}
        <ViewSmall>
          <>
            <div className="secondary-header-v2-mobie">
              <div className='secondary-header-v2-hero-container'>
                <HeaderBrandLogo {...{ ...headerBrandLogoProps, gaTracking: undefined, href: false }} />
                <LozengeMessageV2 {...lozengeMessageProps} />
              </div>
              <TertiaryButton {...menuButtonProps} />
            </div>
            <UitkSheet
              isVisible={isMobileMenuVisible}
              type="side"
              triggerRef={triggerRef}
              className="mobile-menu-sheet"
              sideSheetLayout={{
                position: 'trailing',
                columns: 12,
              }}
            >
              <UitkToolbar
                navigationContent={{
                  onClick: () => setIsMobileVisible(false),
                  navIconLabel: 'Close Mobile Menu',
                }}
                toolbarTitle={'Close'}
                className="mobile-menu-close"
              />
              <UitkSheetContent className="mobile-menu-sheet-content">
                <a className="home-button menu-item" onClick={() => goToHome()}>
                  Home
                </a>
                {SubMenuDropDown('mobile', multiMenuItems, linkNavigationMethod)}
              </UitkSheetContent>
            </UitkSheet>
          </>
        </ViewSmall>
        {/* desktop secondary header device > 992 */}
        <ViewLarge>
          <>
            <div className="secondary-header-v2">
              <div className='secondary-header-v2-hero-container'>
                <HeaderBrandLogo {...{ ...headerBrandLogoProps, gaTracking: undefined, href: false }} />
                <LozengeMessageV2 {...lozengeMessageProps} />
              </div>
              <div className="secondary-header-v2-content">
                <a className="home-button menu-item" onClick={() => goToHome()}>
                  Home
                </a>
                {SubMenuDropDown('desktop', multiMenuItems, linkNavigationMethod)}
              </div>
            </div>
          </>
        </ViewLarge>
      </Viewport>
    </Layout>
  );
};

export default SubHeadervariant2;
